import { Settings } from '@streamr/tv-engine';
import { EventEmitter } from 'eventemitter3';
import { DefaultVideoConstraints } from '../../consts';
import { executeSoon } from '../../utils/executeSoon';
import { ChosenMediaStore } from './ChosenMediaStore';
import { MediaDeviceList } from './MediaDeviceList';
import { MediaServiceOutput } from './MediaServiceOutput';
export class MediaService extends EventEmitter {
    // Singleton pattern implementation
    static instance;
    // Return the full singleton instance of the MediaService. Do not use this
    // method in React components.
    static getInstance() {
        if (!MediaService.instance) {
            MediaService.instance = new MediaService();
        }
        return MediaService.instance;
    }
    /**
     * @returns A notification interface that contains only void-returning methods
     * to be used in React components.
     */
    static getNotificationInterface() {
        return MediaService.getInstance();
    }
    /**
     * @returns A set of reactive getters to be used in React components.
     */
    static getOutput() {
        return MediaServiceOutput.getReactiveOutput();
    }
    // ------------- Singleton implementation end
    output;
    outputMediaStream;
    permissionedAudioDevices;
    permissionedVideoDevices;
    connectedVideoDevices;
    connectedAudioDevices;
    mediaStream;
    displayMediaStream;
    audioMuted = false;
    videoMuted = false;
    captureStarted = false;
    constructor() {
        super();
        this.output = new MediaServiceOutput();
        this.permissionedAudioDevices = new MediaDeviceList();
        this.permissionedVideoDevices = new MediaDeviceList();
        this.connectedVideoDevices = new MediaDeviceList();
        this.connectedAudioDevices = new MediaDeviceList();
        // Listen to device add/remove events
        if (navigator.mediaDevices) {
            navigator.mediaDevices.addEventListener('devicechange', () => {
                this.updatePermissionedDevices().catch((error) => {
                    console.error('Error updating devices', error);
                });
                this.updateConnectedMediaDevices().catch((error) => {
                    console.error('Error updating connected devices', error);
                });
            });
        }
        this.updatePermissionedDevices().catch((error) => {
            console.error('Error initializing permissions', error);
        });
        this.updateConnectedMediaDevices().catch((error) => {
            console.error('Error updating connected devices', error);
        });
    }
    async init() {
        // ToDo: Implement initialization
    }
    getChosenAudioDevice() {
        const chosenAudioDevice = ChosenMediaStore.getInstance().getChosenAudioDevice();
        if (chosenAudioDevice &&
            this.permissionedAudioDevices.has(chosenAudioDevice)) {
            return chosenAudioDevice;
        }
        if (this.permissionedAudioDevices.getDevices().length > 0) {
            const device = this.permissionedAudioDevices.getDevices()[0];
            ChosenMediaStore.getInstance().setChosenAudioDevice(device);
            return device;
        }
        return undefined;
    }
    getChosenVideoDevice() {
        const chosenVideoDevice = ChosenMediaStore.getInstance().getChosenVideoDevice();
        if (chosenVideoDevice &&
            this.permissionedVideoDevices.has(chosenVideoDevice)) {
            return chosenVideoDevice;
        }
        if (this.permissionedVideoDevices.getDevices().length > 0) {
            const device = this.permissionedVideoDevices.getDevices()[0];
            ChosenMediaStore.getInstance().setChosenVideoDevice(device);
            return device;
        }
        return undefined;
    }
    async updatePermissionedDevices() {
        const devices = await navigator.mediaDevices.enumerateDevices();
        // If a device has no label, we do not have permission to use it,
        // so we filter them out
        const audioDevices = devices.filter((device) => {
            if (device.kind === 'audioinput') {
                return device.label && device.label.length > 0;
            }
            return false;
        });
        const videoDevices = devices.filter((device) => {
            if (device.kind === 'videoinput') {
                return device.label && device.label.length > 0;
            }
            return false;
        });
        // Update the devices in the output state if they have changed
        const audioDevicesChanged = this.permissionedAudioDevices.updateDevices(audioDevices);
        if (audioDevicesChanged) {
            if (this.permissionedAudioDevices.getDevices().length > 0) {
                this.output.setMicrophonePermissioned(true);
            }
            else {
                this.output.setMicrophonePermissioned(false);
            }
            this.output.setAudioDevices(this.permissionedAudioDevices.getDevices());
        }
        const videoDevicesChanged = this.permissionedVideoDevices.updateDevices(videoDevices);
        if (videoDevicesChanged) {
            if (this.permissionedVideoDevices.getDevices().length > 0) {
                this.output.setCameraPermissioned(true);
            }
            else {
                this.output.setCameraPermissioned(false);
            }
            this.output.setVideoDevices(this.permissionedVideoDevices.getDevices());
        }
        // If the devices have changed, update the media stream
        if (audioDevicesChanged || videoDevicesChanged) {
            await this.updateMediaStream();
        }
    }
    async updateConnectedMediaDevices() {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const audioDevices = devices.filter((device) => device.kind === 'audioinput');
        const videoDevices = devices.filter((device) => device.kind === 'videoinput');
        this.connectedAudioDevices.updateDevices(audioDevices);
        this.connectedVideoDevices.updateDevices(videoDevices);
        this.output.setConnectedAudioDevices(this.connectedAudioDevices.getDevices());
        this.output.setConnectedVideoDevices(this.connectedVideoDevices.getDevices());
    }
    async requestMediaPermissions({ audio = false, video = false, }) {
        const mediaStream = await navigator.mediaDevices.getUserMedia({
            audio,
            video: video ? DefaultVideoConstraints : false,
        });
        // We don't need the media stream, stop all tracks
        for (const track of mediaStream.getTracks()) {
            track.stop();
        }
        await this.updatePermissionedDevices();
        // await this.updateMediaStream();
    }
    publishMediaStream() {
        const tracks = [];
        // Check if display media tracks are still active (not ended by browser's stop sharing)
        const activeDisplayAudioTracks = this.displayMediaStream
            ?.getAudioTracks()
            .filter((track) => track.readyState === 'live') || [];
        const activeDisplayVideoTracks = this.displayMediaStream
            ?.getVideoTracks()
            .filter((track) => track.readyState === 'live') || [];
        // If no active display tracks, clear the display media stream
        if (this.displayMediaStream &&
            activeDisplayAudioTracks.length === 0 &&
            activeDisplayVideoTracks.length === 0) {
            this.displayMediaStream = undefined;
        }
        // Prioritize active display media stream video tracks
        if (activeDisplayVideoTracks.length > 0) {
            tracks.push(...activeDisplayVideoTracks);
        }
        else if (this.mediaStream &&
            this.mediaStream.getVideoTracks().length > 0) {
            tracks.push(...this.mediaStream.getVideoTracks());
        }
        // Prioritize active display media stream audio tracks
        if (activeDisplayAudioTracks.length > 0) {
            tracks.push(...activeDisplayAudioTracks);
        }
        else if (this.mediaStream &&
            this.mediaStream.getAudioTracks().length > 0) {
            tracks.push(...this.mediaStream.getAudioTracks());
        }
        this.outputMediaStream = new MediaStream(tracks);
        const videoTrack = this.outputMediaStream.getVideoTracks()[0];
        const isUserFacingVideo = videoTrack &&
            (videoTrack.getSettings().facingMode === 'user' ||
                /facetime hd camera|integrated/i.test(videoTrack.label));
        this.output.setMediaStreamData({
            mediaStream: this.outputMediaStream,
            isUserFacingVideo: isUserFacingVideo,
            isSharingScreen: this.displayMediaStream !== undefined,
            // Check active display media tracks, not just their existence
            isSharingScreenAudio: activeDisplayAudioTracks.length > 0,
            isSharingScreenVideo: activeDisplayVideoTracks.length > 0,
            hasAudio: this.outputMediaStream.getAudioTracks().length > 0,
            hasVideo: this.outputMediaStream.getVideoTracks().length > 0,
            hasTracks: this.outputMediaStream.getTracks().length > 0,
        });
        this.emit('mediaStreamChanged');
    }
    async updateMediaStream() {
        const chosenAudioDevice = this.getChosenAudioDevice();
        const chosenVideoDevice = this.getChosenVideoDevice();
        if (chosenAudioDevice) {
            this.output.setSelectedAudioDevice(chosenAudioDevice);
        }
        if (chosenVideoDevice) {
            this.output.setSelectedVideoDevice(chosenVideoDevice);
        }
        if (!this.captureStarted) {
            return;
        }
        let tempMediaStream = new MediaStream();
        this.mediaStream?.getTracks().forEach((track) => track.stop());
        if (!(this.audioMuted && this.videoMuted) &&
            (chosenAudioDevice || chosenVideoDevice)) {
            tempMediaStream = await navigator.mediaDevices.getUserMedia({
                audio: this.audioMuted || !chosenAudioDevice
                    ? false
                    : {
                        deviceId: {
                            exact: chosenAudioDevice.deviceId,
                        },
                    },
                video: this.videoMuted || !chosenVideoDevice
                    ? false
                    : {
                        ...DefaultVideoConstraints,
                        deviceId: {
                            exact: chosenVideoDevice.deviceId,
                        },
                    },
            });
        }
        this.mediaStream = tempMediaStream;
        this.publishMediaStream();
    }
    async updateDisplayMediaStream(surfaceType) {
        this.displayMediaStream = await navigator.mediaDevices.getDisplayMedia({
            video: {
                displaySurface: surfaceType,
                frameRate: {
                    ideal: Settings.videoEncoderDefaultFrameRate,
                },
            },
            audio: {
                sampleRate: Settings.audioSampleRate,
                suppressLocalAudioPlayback: true,
            },
        });
        for (const track of this.displayMediaStream.getTracks()) {
            track.isScreenShare = true;
            // Listen for track ended events (browser's stop sharing button)
            track.addEventListener('ended', () => {
                this.publishMediaStream();
            });
        }
        if (this.displayMediaStream.getVideoTracks().length > 0) {
            const videoTrack = this.displayMediaStream.getVideoTracks()[0];
            videoTrack.applyConstraints({
                frameRate: {
                    min: Settings.videoEncoderDefaultFrameRate,
                },
            });
        }
        this.publishMediaStream();
    }
    // Getter for other services
    getOutputMediaStream() {
        return this.outputMediaStream;
    }
    // Notifications to be called by React components
    notifyAudioDeviceSelected(audioDevice) {
        if (ChosenMediaStore.getInstance().getChosenAudioDevice() !==
            audioDevice) {
            ChosenMediaStore.getInstance().setChosenAudioDevice(audioDevice);
            this.updateMediaStream().catch((e) => {
                console.error(e);
            });
        }
    }
    notifyVideoDeviceSelected(videoDevice) {
        if (ChosenMediaStore.getInstance().getChosenVideoDevice() !==
            videoDevice) {
            ChosenMediaStore.getInstance().setChosenVideoDevice(videoDevice);
            this.updateMediaStream().catch((e) => {
                console.error(e);
            });
        }
    }
    notifyMediaPermissionsRequested({ audio = false, video = false, }) {
        this.requestMediaPermissions({ audio, video }).catch((e) => {
            console.error(e);
        });
    }
    notifyAudioMutingChangeRequested(muted) {
        if (this.audioMuted !== muted) {
            this.audioMuted = muted;
            this.output.setAudioMuted(muted);
            this.updateMediaStream().catch((e) => {
                console.error(e);
            });
        }
    }
    notifyVideoMutingChangeRequested(muted) {
        if (this.videoMuted !== muted) {
            this.videoMuted = muted;
            this.output.setVideoMuted(muted);
            this.updateMediaStream().catch((e) => {
                console.error(e);
            });
        }
    }
    notifyStartCaptureRequested() {
        if (this.captureStarted) {
            return;
        }
        this.captureStarted = true;
        executeSoon(() => this.updateMediaStream());
    }
    notifyStopCaptureRequested() {
        this.captureStarted = false;
        this.mediaStream?.getTracks().forEach((track) => {
            track.stop();
        });
        this.mediaStream = undefined;
        this.displayMediaStream?.getTracks().forEach((track) => {
            track.stop();
        });
        this.displayMediaStream = undefined;
        this.publishMediaStream();
    }
    notifyScreenShareRequested(surfaceType) {
        this.updateDisplayMediaStream(surfaceType).catch((e) => {
            console.error(e);
        });
    }
    notifyScreenShareStopped() {
        this.displayMediaStream?.getTracks().forEach((track) => {
            track.stop();
        });
        this.displayMediaStream = undefined;
        this.publishMediaStream();
    }
}
